import IndexStore from 'src/stores/index';
import type {DynamicLabels} from 'src/action-creators/dynamic-labels';
import type {EventTypes} from 'src/types/events';
import type {BeefreeModule} from 'src/components/workflow/event/beefree-content';
import type {
  BeefreeDocument,
  OnChangeResponse,
} from 'src/components/lib/beefree-editor';

import * as React from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {
  surveyContentToken,
  jobMatchContentToken,
  chatLinkToken,
  surveyLinkEmailToken,
} from 'src/components/workflow/event/constants';
import {ApiError} from 'src/utils/redux-api-v2';
import parsers from 'src/api-parsers/events';
import {useJobMatchEnabledForEngage} from 'src/hooks/useAgencyConfig';
import {camel} from 'src/utils';

import {
  pushModal,
  showApiError,
  showGenericError,
} from 'src/action-creators/modal';
import {selectWorkflowEntityMapping} from 'src/selectors/dynamic-labels';
import {
  getEntityTypeMappings,
  eventEntityType as getEventEntityType,
} from 'src/selectors/ats-entities';
import {getCurrentAgent} from 'src/selectors/accounts';
import {getCurrentWorkflowEntityType} from 'src/selectors/workflow';
import {getSearchableEntity} from 'src/utils/entities';

import SegmentedButton from 'src/components/lib/segmented-button';
import Toggle from 'src/components/lib/toggle/toggle';

import * as api from 'src/utils/api-no-store';
import {captureSentryMessage} from 'src/utils/sentry';
import logger from 'src/utils/logger';

import BeefreeEditor from 'src/components/lib/beefree-editor';
import UserSearch from 'src/components/lib/user-token-search';
import BasicDropdown from 'src/components/lib/basic-dropdown';
import PreviewIframe from 'src/components/lib/preview-iframe';

import {useMockedEntities, useCandidateForEntity} from './mocked-entity-hook';

import css from './beefree.css';


type MarketingProps = {
  fluxStore: IndexStore;
  moduleData: BeefreeModule;
  dynamicLabels: DynamicLabels;
  eventType: EventTypes;
  onChange: (
    beefree_json: string,
    beefree_html?: string,
    data?: BeefreeModule,
  ) => BeefreeDocument;
};

export default function BeeFreeEditor(
  props: MarketingProps,
): React.ReactElement<React.ComponentProps<'div'>> {
  const {fluxStore, moduleData, dynamicLabels, eventType} = props;

  const dispatch = useDispatch();
  const eventEntityType = useSelector(getEventEntityType);
  const jobMatchEnabled = useJobMatchEnabledForEngage();
  const randomEntity = useCandidateForEntity(eventEntityType);
  const [beefreeJson, setBeefreeJson] = React.useState(moduleData.beefree_json);
  const [beefreeHtml, setBeefreeHtml] = React.useState(moduleData.beefree_html);
  const [beeChangeLog, setBeeChangelog] = React.useState([]);

  // @ts-expect-error - TS2554 - Expected 1 arguments, but got 0.
  const editorRef = React.useRef();

  // @ts-expect-error - TS2554 - Expected 2 arguments, but got 1.
  const onChange = React.useCallback(
    (beefree_json_doc: string, response?: OnChangeResponse) => {
      // We can inspect the changelog for possible issues, for now, let's just
      // keep the last five emitted change events
      // @ts-expect-error - TS2345 - Argument of type '(prev: never[]) => (OnChangeResponse | undefined)[]' is not assignable to parameter of type 'SetStateAction<never[]>'.
      setBeeChangelog((prev) => [...prev.slice(-4), response]);
      if (
        jobMatchEnabled &&
        checkForString(beefree_json_doc, surveyContentToken) > 1
      ) {
        dispatch(
          showGenericError({
            title: 'Too many surveys/jobs',
            text: 'Looks like you included another Survey/Job List in your email. Please remove all but one of them before proceeding',
          }),
        );
      }

      if (checkForString(beefree_json_doc, surveyContentToken) > 1) {
        dispatch(
          showGenericError({
            title: 'Too many surveys',
            text: jobMatchEnabled
              ? 'Looks like you included another Survey/Job List in your email. Please remove all but one of them before proceeding'
              : 'Looks like you just included another survey link in your email. Please remove all but one of them before proceeding.',
          }),
        );
      }

      const beefree_json = JSON.parse(beefree_json_doc);

      if (
        beefree_json?.page?.title === 'Template Base' ||
        beefree_json?.page?.description === 'Test template for BEE' ||
        beefree_json?.page?.template.version === '0.0.1'
      ) {
        // something has caused the template to be cleared, let's emit the changelog to sentry
        captureSentryMessage('got reverted beefree template', {
          extra: {
            changelog: beeChangeLog,
            beefreeJson, // this will likely be clipped by the 200k limit
          },
        });

        // maybe attempt to reload the existing document discarding this bad change
        // @ts-expect-error - TS2339 - Property 'handleLoad' does not exist on type '{}'.
        editorRef.current?.handleLoad(beefreeJson);

        logger.log(
          `an error occurred while modifying following beefree document`,
        );
        logger.error(`changelog: ${JSON.stringify(beeChangeLog)}`);
        logger.error(
          `beefree doc before last change: ${JSON.stringify(beefreeJson)}`,
        );
        logger.error(`beefree doc after last change: ${beefree_json_doc}`);

        return;
      }

      const jsonDoc = props.onChange(beefree_json_doc, undefined, moduleData);

      setBeefreeJson(jsonDoc);
    },
  );

  // this is a misnomer, this callback flushes the current jsondoc embedded
  // in the editor even if it hasn't been flushed by an onChange event
  // @ts-expect-error - TS2554 - Expected 2 arguments, but got 1.
  const onSaveAsTemplate = React.useCallback((beefree_json_doc: string) => {
    const jsonDoc = props.onChange(beefree_json_doc, undefined, moduleData);
    setBeefreeJson(jsonDoc);
  });

  // These are test methods that we attach to window during the lifecycle
  // of the editor so that we can debug or manually interact with the editor
  // instance
  React.useEffect(() => {
    window.__updateBeefreeDoc = (doc: string) => onChange(doc);
    window.__getBeefreeDoc = () => beefreeJson;
    window.__loadBeefreeDoc = (doc: BeefreeDocument) =>
      // @ts-expect-error - TS2339 - Property 'handleLoad' does not exist on type '{}'.
      editorRef.current?.handleLoad(doc);
    return () => {
      delete window.__updateBeefreeDoc;
      delete window.__getBeefreeDoc;
      delete window.__loadBeefreeDoc;
    };
  }, [beefreeJson]);

  const mainEntityType = useSelector(selectWorkflowEntityMapping).display_name;

  const labelHandler = React.useCallback(
    // @ts-expect-error - TS7006 - Parameter 'resolve' implicitly has an 'any' type. | TS7006 - Parameter 'reject' implicitly has an 'any' type.
    (resolve, reject) => {
      dispatch(
        pushModal({
          type: 'VARIABLE_PICKER',
          // @ts-expect-error - TS2353 - Object literal may only specify known properties, and 'labels' does not exist in type 'BasicModal'.
          labels: dynamicLabels,
          baseEntityType: mainEntityType,
          onClose: () => reject(),
          // @ts-expect-error - TS7006 - Parameter 'label' implicitly has an 'any' type. | TS7006 - Parameter 'evt' implicitly has an 'any' type.
          insertResult: (label, evt) => {
            if (evt) {
              evt.preventDefault();
              evt.stopPropagation();
            }
            resolve({name: label.name, value: `{{${label.value}}}`});
          },
        }),
      );
    },
    [dispatch, dynamicLabels, mainEntityType],
  );

  const [previewing, setPreviewing] = React.useState(false);
  const [structureVisible, setStructureVisible] = React.useState(false);
  // $FlowOptionalCall
  useDeferredEffect(
    // @ts-expect-error - TS2339 - Property 'toggleStructure' does not exist on type '{}'.
    () => editorRef.current?.toggleStructure(),
    [structureVisible],
  );

  const [previewSize, setPreviewSize] = React.useState('desktop');
  const {workflowId, eventId} = useSelector((state) => state.route.params);
  // @ts-expect-error - TS2339 - Property 'id' does not exist on type 'Account | AuthedUserAccount | null | undefined'.
  const {id: currentUserId} = useSelector(getCurrentAgent);
  // change this to default to beefreeHtml for faster initial preview
  const [previewdoc, setPreviewDoc] = React.useState(beefreeHtml);

  const [mockedEntityId, setMockedEntityId] = React.useState(randomEntity?.id);

  const [waitingForPreview, setWaitingForPreview] = React.useState(false);

  // force the bee to 'save' and flush the beefreejson into the
  // onSaveTemplate callback
  const flushBeeEditor = React.useCallback(() => {
    // @ts-expect-error - TS2339 - Property 'handleSaveTemplate' does not exist on type '{}'.
    editorRef.current?.handleSaveTemplate();
  }, [editorRef]);

  const handleBuildPreviewToggle = async (evt: any) => {
    const isPreviewing = evt.currentTarget.value === 'preview';
    if (isPreviewing !== previewing) {
      if (isPreviewing) {
        // @ts-expect-error - TS2339 - Property 'handleSaveTemplate' does not exist on type '{}'.
        editorRef.current?.handleSaveTemplate();
        handleBuildPreview(mockedEntityId);
      }
      setPreviewing(isPreviewing);
    }
  };

  //update mocked_entity_id with the first entity id
  React.useEffect(() => {
    if (randomEntity) {
      setMockedEntityId(randomEntity?.id);
    }
  }, [randomEntity]);

  const handleBuildPreview = async (mockedEntityId?: string | null) => {
    setWaitingForPreview(true);
    let latestHtml = beefreeHtml;
    try {
      latestHtml = await api.post('workflows/beefree-html', {
        beefree_json: beefreeJson,
      });
      setBeefreeHtml(latestHtml);
      const normalized = parsers.normalize.event(
        fluxStore.eventCreation.state.event,
        jobMatchEnabled,
      );
      const temporary_event = {
        id: 'foo',
        modules: normalized.modules,
        beefree_json: normalized.beefree_json,
        content_subscription_category_id:
          normalized.content_subscription_category_id,
        content_subscription_reason: normalized.content_subscription_reason,
        skip_subscription_link: normalized.skip_subscription_link,
      } as const;

      const payload = {
        temporary_event,
        mocked_entity_id: undefined,
      } as const;
      if (mockedEntityId !== null) {
        // @ts-expect-error - TS2540 - Cannot assign to 'mocked_entity_id' because it is a read-only property.
        payload['mocked_entity_id'] = mockedEntityId;
      }
      const previewHtml = await api.post(
        `workflows/${workflowId}/events/${eventId}/preview`,
        payload,
      );

      setPreviewDoc(previewHtml);
    } catch (error: any) {
      if (error instanceof ApiError) {
        dispatch(
          showApiError(error, 'There was an issue generating the preview.'),
        );
      } else {
        throw error;
      }
    } finally {
      setWaitingForPreview(false);
    }
  };

  return (
    <div style={{flexFlow: 'column', width: 1025, minWidth: 1025}}>
      <div>
        <SegmentedButton
          options={[
            {label: 'Build Email', value: 'build'},
            {label: 'Preview Email', value: 'preview'},
          ]}
          value={previewing ? 'preview' : 'build'}
          onChange={handleBuildPreviewToggle}
          className={css.buildPreviewToggle}
        />
      </div>
      {previewing && (
        <div className={css.previewContainer}>
          <div className={css.previewControls}>
            {/* @ts-expect-error - TS2741 - Property 'className' is missing in type '{ options: { label: string; value: string; }[]; value: string; onChange: (evt: SyntheticEvent<HTMLInputElement & { currentTarget: { value: string; }; }, Event>) => void; }' but required in type 'Props<string>'. */}
            <SegmentedButton
              options={[
                {label: 'Desktop', value: 'desktop'},
                {label: 'Mobile', value: 'mobile'},
              ]}
              value={previewSize}
              onChange={(evt) => setPreviewSize(evt.currentTarget.value)}
            />

            <PreviewControls
              eventEntityType={eventEntityType}
              // @ts-expect-error - TS2322 - Type 'Dispatch<SetStateAction<string | undefined>>' is not assignable to type '(value?: string | null | undefined) => any'.
              onEntityChange={setMockedEntityId}
              defaultEntity={camel(randomEntity)}
            />

            <div>
              <button
                className={css.button}
                disabled={waitingForPreview}
                onClick={() => handleBuildPreview(mockedEntityId)}
              >
                {waitingForPreview
                  ? 'Building preview...'
                  : 'Preview this email'}
              </button>
            </div>
          </div>
          <div className={css.previewContent}>
            <FakeBrowser
              className={
                previewSize === 'desktop' ? css.iframeDesktop : css.iframeMobile
              }
            >
              <PreviewIframe
                srcdoc={previewdoc}
                name="beefree-iframe-preview"
                previewSize={previewSize}
                className={
                  previewSize === 'desktop'
                    ? css.iframeDesktop
                    : css.iframeMobile
                }
              />
            </FakeBrowser>
          </div>
        </div>
      )}
      <div className={previewing ? css.hiddenBeeFree : css.beeFreeEditorBlock}>
        <div className={css.structureToggleRow}>
          <span
            className={css.structureToggleLabel}
            onClick={() => setStructureVisible((prev) => !prev)}
          >
            Show Structure
          </span>
          <Toggle
            className={css.structureToggle}
            checked={structureVisible}
            onChange={() => {
              setStructureVisible((prev) => !prev);
              // editorRef.current.toggleStructure();
            }}
          />
        </div>
        <div>
          <BeefreeEditor
            uid={currentUserId}
            // @ts-expect-error - TS2769 - No overload matches this call.
            ref={editorRef}
            onSaveAsTemplate={onSaveAsTemplate}
            onChange={onChange}
            trackChanges={true}
            initialTemplate={moduleData.beefree_json}
            contentDialog={{
              mergeTags: {
                label: 'Insert dynamic label',
                handler: labelHandler,
              },
            }}
            addOns={[{enabled: false, id: 'sense-scheduler-row'}]}
            mergeContents={
              eventType === 'beefree_chatbot'
                ? []
                : [
                    {
                      name: jobMatchEnabled
                        ? 'THE SURVEY AND/OR JOB LIST WILL APPEAR HERE'
                        : 'Survey Content',
                      value: jobMatchEnabled
                        ? jobMatchContentToken
                        : surveyContentToken,
                    },
                  ]
            }
            specialLinks={[
              {
                type: 'Opt Outs',
                label: 'raw unsubscribe url',
                link: '<%asm_global_unsubscribe_raw_url%>',
              },
              eventType === 'beefree_chatbot'
                ? {
                    type: 'Chatbot',
                    label: 'link to chatbot',
                    link: chatLinkToken,
                  }
                : {
                    type: 'Survey',
                    label: 'link to survey',
                    link: surveyLinkEmailToken,
                  },
            ]}
            translations={{
              'mailup-bee-newsletter-modules-merge-content': {
                name: jobMatchEnabled ? 'Survey / Jobs' : 'Survey',
              },
            }}
          />
        </div>
      </div>
    </div>
  );
}

// Run an effect when args change but not during mount
function useDeferredEffect(effectFn: () => unknown, args: Array<boolean>) {
  const prev = React.useRef(null);

  React.useEffect(() => {
    if (prev.current !== null && prev.current !== args) {
      effectFn();
    }
    // @ts-expect-error - TS2322 - Type 'boolean[]' is not assignable to type 'null'.
    prev.current = args;
  }, args);
}

export function checkForString(json: string, string: string): number {
  return json.match(new RegExp(string, 'g'))?.length ?? 0;
}

function FakeBrowser({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={className}
      style={{
        flex: 1,
        flexFlow: 'column',
        marginTop: 20,
        border: '1px solid #ddd',
        borderRadius: 6,
        overflow: 'hidden',
      }}
    >
      <div style={{background: 'white', padding: '11px 12px 12px'}}>
        <span
          style={{
            width: 12,
            height: 12,
            borderRadius: 6,
            border: '1px solid #C4C4C4',
            marginRight: 8,
          }}
        />
        <span
          style={{
            width: 12,
            height: 12,
            borderRadius: 6,
            border: '1px solid #C4C4C4',
            marginRight: 8,
          }}
        />
        <span
          style={{
            width: 12,
            height: 12,
            borderRadius: 6,
            border: '1px solid #C4C4C4',
            marginRight: 8,
          }}
        />
      </div>
      <div style={{background: '#ddd', justifyContent: 'center'}}>
        {children}
      </div>
    </div>
  );
}

function PreviewControls({
  eventEntityType,
  onEntityChange,
  defaultEntity,
}: {
  eventEntityType: string;
  onEntityChange: (value?: string | null | undefined) => any;
  defaultEntity:
    | {
        id: string;
      }
    | null
    | undefined;
}) {
  const [previewingAudienceId, setPreviewingAudienceId] = React.useState(
    (defaultEntity && defaultEntity.id) || null,
  );
  const [previewingPlacementId, setPreviewingPlacementId] =
    React.useState<any>(null);

  const {loading, mockedEntityId, relatedEntities} = useMockedEntities(
    previewingAudienceId,
    previewingPlacementId,
    eventEntityType,
  );

  React.useEffect(() => {
    mockedEntityId && onEntityChange && onEntityChange(mockedEntityId);
  }, [mockedEntityId, onEntityChange]);
  const relatedEntitiesDropdownOptions =
    relatedEntities &&
    // @ts-expect-error - TS2339 - Property 'map' does not exist on type 'never'. | TS7006 - Parameter 'entity' implicitly has an 'any' type.
    relatedEntities.map((entity) => ({
      value: entity.id,
      label: `${entity.company_name ? entity.company_name + ' ' : ''}(ID: ${
        entity.external_source_id
      })`,
    }));

  const atsEntityMappings = useSelector(getEntityTypeMappings);

  const workflowEntityInfo = useSelector(getCurrentWorkflowEntityType);
  const searchableEntityType = getSearchableEntity(
    atsEntityMappings,
    // @ts-expect-error - TS2345 - Argument of type 'AtsEntity | null | undefined' is not assignable to parameter of type 'AtsEntity'.
    workflowEntityInfo,
  )?.name;

  return (
    <div className={css.previewControls}>
      <span className={css.selectLabel}>Viewing as:</span>
      <UserSearch
        // @ts-expect-error - TS2322 - Type 'EntityType | undefined' is not assignable to type 'EntityType'.
        entityType={searchableEntityType}
        onSearchResultClick={(entityId) => {
          setPreviewingAudienceId(entityId);
        }}
        defaultValues={defaultEntity ? [defaultEntity] : []}
        className={css.userSearch}
        showsValue={true}
        placeholder="Jana Ally"
        onClearSelection={() => setPreviewingAudienceId(null)}
      />

      {Array.isArray(relatedEntitiesDropdownOptions) &&
        // @ts-expect-error - TS2339 - Property 'length' does not exist on type 'never'.
        relatedEntitiesDropdownOptions.length > 0 && (
          <React.Fragment>
            <span className={css.selectLabel}>
              {/* @ts-expect-error - TS18049 - 'workflowEntityInfo' is possibly 'null' or 'undefined'. */}
              {`${workflowEntityInfo.display_name}`}:
            </span>

            {/* @ts-expect-error - TS2741 - Property 'qaId' is missing in type '{ options: never; onChange: (entity: object) => void; selected: any; noOptionText: string; }' but required in type 'BasicDropdownProps'. */}
            <BasicDropdown
              options={relatedEntitiesDropdownOptions}
              onChange={(entity) => {
                // @ts-expect-error - TS2339 - Property 'value' does not exist on type 'object'.
                setPreviewingPlacementId(entity.value);
              }}
              // @ts-expect-error - TS2339 - Property 'find' does not exist on type 'never'.
              selected={relatedEntitiesDropdownOptions.find(
                // @ts-expect-error - TS7006 - Parameter 'option' implicitly has an 'any' type.
                (option) => mockedEntityId === option.value,
              )}
              noOptionText={loading ? 'Loading' : 'no results'}
            />
          </React.Fragment>
        )}
    </div>
  );
}
